import { catchError, combineLatest, EMPTY, Subscription, throwError, timer } from 'rxjs'
import { filter, map, switchMap, take } from 'rxjs/operators'

import { Component, HostListener, OnDestroy, ViewChild, ViewContainerRef } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { AuthenticationService, isInteractiveAuthRequired } from '@services/authentication.service'
import { ContextMenuService } from '@services/context-menu.service'
import { EnvironmentManagerService } from '@services/environment-manager.service'
import { ModalService } from '@services/modal.service'
import { ProductAnalyticsService } from '@services/product-analytics.service'
import { SidebarService } from '@services/sidebar.service'
import { SubscriptionService } from '@services/subscription.service'
import { UserService } from '@services/user.service'
import { NavigationHistoryService } from './services/navigation-history.service'
import { RuntimeCapabilitiesService } from './services/runtime-capabilities.service'

import { environment } from 'src/environments/environment'
import { ContextMenuDirective } from './directives/context-menu.directive'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnDestroy {
  private static readonly AUTH_CALLBACK_LOADING_TIMEOUT_MS = 10_000
  private static readonly STYLE_GUIDE_DOCUMENT_CLASS = 'style-guide-document'
  private readonly styleGuideDocumentStyleKeys = [
    'position',
    'height',
    'width',
    'overflow-y',
    'cursor'
  ]
  private styleGuideRouteSub?: Subscription

  @ViewChild(ContextMenuDirective, { static: true }) contextMenu!: ContextMenuDirective;

  get currentModule() { return this.authenticationService.currentModule }
  get currentUser$() { return this.userService.currentUser$ }
  get user$() { return this.authenticationService.user$ }
  get isMobile() { return this.environmentManager.isMobile }
  get windowLocationIsNotStatic404() { return window.location.pathname !== '/error/404' }
  get shouldShowSidebar() {
    if (this.isStyleGuideRoute || this._router.url.startsWith('/organization-access')) return false
    // Show sidebar on main app pages, hide on builder/viewer/login/signup
    const module = this.currentModule
    return module !== '' && module !== 'builder' && module !== 'viewer' && module !== 'model-editor'
  }
  get isStyleGuideRoute() {
    return !environment.production && (window.location.pathname === '/design-system' || this._router.url.startsWith('/dev/style-guide')
      || (typeof window !== 'undefined' && window.location.pathname.startsWith('/dev/style-guide')))
  }
  bothUsers$ = combineLatest([this.user$, this.currentUser$]).pipe(
    map(([user, currentUser]) => ({
      ...user,
      canResetPassword: user?.['https://kartorium.com/authentication_strategy'] === 'auth0',
      name: currentUser.name,
      picture: currentUser.picture,
      employeeNumber: currentUser.employeeNumber,
    }))
  )
  get isSafari() {
    const agent = navigator.userAgent
    return !agent.includes('Chromium') && !agent.includes('Chrome') && agent.includes('Safari')
  }

  public editing: boolean = false
  public isAuthCallbackRecoveryVisible = false

  constructor(
    private _router: Router,
    private _subscriptionService: SubscriptionService,
    public authenticationService: AuthenticationService,
    public contextMenuService: ContextMenuService,
    public environmentManager: EnvironmentManagerService,
    public modalService: ModalService,
    private productAnalyticsService: ProductAnalyticsService,
    public sidebarService: SidebarService,
    public userService: UserService,
    public viewContainerRef: ViewContainerRef,
    // Eagerly create NavigationHistoryService so it counts the very first
    // NavigationEnd. Without this, the service is lazy-instantiated by the
    // first detail-page-shell on the page, missing the initial nav — which
    // makes canGoBack() return false until the user has navigated twice
    // inside the detail-page. The shell then falls back to router.navigate
    // (imperative) instead of Location.back() (popstate), and the
    // ListPageReuseStrategy can't restore the cached list page.
    _navHistory: NavigationHistoryService,
    public capabilities: RuntimeCapabilitiesService,
  ) {
    this.syncStyleGuideDocumentScroll()
    this.styleGuideRouteSub = this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ).subscribe(() => this.syncStyleGuideDocumentScroll())

    if (this.isStyleGuideRoute) {
      this._router.initialNavigation()
      return
    }
    // A static missing-page response does not depend on API availability.
    if (window.location.pathname === '/error/404') this._router.initialNavigation()
    this.retryCapabilities()
    combineLatest([
      timer(0, 30 * 60 * 1000), // Once immediately and repeats every 30 minutes
      this.authenticationService.isLoading$,
      this.authenticationService.isAuthenticated$,
    ]).pipe(
      switchMap(([, isLoading, isAuthenticated]) => {
        if (isLoading || !isAuthenticated || this._router.url.startsWith('/organization-access') || window.location.pathname === '/organization-access') return EMPTY

        return this.authenticationService.createAccessToken().pipe(
          catchError(err => {
            if (isInteractiveAuthRequired(err)) {
              this.startInteractiveReauthentication()
              return EMPTY
            }
            return throwError(() => err)
          }),
        )
      }),
    ).subscribe({
      next: token => localStorage.setItem('accessToken', token),
      error: err => console.error('Failed to refresh access token:', err),
    })

    this.startAuthCallbackRecoveryWatch()

    // Auth0 SDK raises errors out-of-band of any HTTP call (e.g. an interceptor's
    // getAccessTokenSilently() failing). Subscribe so the same recovery flow
    // starts regardless of where the error originated.
    this.authenticationService.error$.subscribe(err => {
      if (this.isAuthRedirectCallback()) {
        this.showAuthCallbackRecovery('auth0_error', this.getAuthCallbackErrorDetail(err))
        return
      }

      if (isInteractiveAuthRequired(err)) this.startInteractiveReauthentication()
    })

    /**
     * Disabling image bitmapping improves performance
     * & reduces crashing experienced on iOS devices
     */
    if (this.isSafari) window.createImageBitmap = undefined

    // Give the ModalService reference to the AppComponent
    this.modalService.initialize(this.viewContainerRef)
    this.productAnalyticsService.initialize()
  }

  ngAfterViewInit(): void {
    this.contextMenuService.container = this.contextMenu.viewContainerRef
  }

  ngOnDestroy(): void {
    this.styleGuideRouteSub?.unsubscribe()
    this.setStyleGuideDocumentMode(false)
  }

  retryCapabilities(): void {
    this.capabilities.load().subscribe({
      next: () => {
        const target = this.capabilities.requestedNavigation
        this.capabilities.requestedNavigation = null
        if (target) this._router.navigateByUrl(target, { replaceUrl: true })
        else this._router.initialNavigation()
      },
      error: () => {}, // The retryable shell owns the unavailable presentation.
    })
  }

  @HostListener('window:resize')
  onWindowResize(): void {}

  resetPassword() {
    this._subscriptionService.resetPassword().subscribe(
      ({ response }) => {
        window.location.href = response
      })
  }

  logout() {
    this.authenticationService.logout()
  }

  /**
   * If silent refresh cannot recover locally, start the normal Auth0 redirect
   * immediately and include the remembered organization when we have one.
   * On /login the LoginComponent owns redirects; skip here to avoid creating
   * a second Auth0 transaction with mismatched state.
   */
  private _reauthRedirectStarted = false
  private startInteractiveReauthentication(): void {
    if (this._reauthRedirectStarted) return
    const target = window.location.pathname + window.location.search
    if (this.isAuthRedirectCallback()) return
    if (this._router.url.startsWith('/login')) return
    if (this._router.url.startsWith('/organization-access')) return
    if (this.isPublicViewerRoute()) {
      this.authenticationService.recordAuthDiagnosticEvent('public_viewer_reauth_suppressed', {
        target,
      })
      return
    }

    const redirectTarget = target && target !== '/' ? target : '/projects'
    this._reauthRedirectStarted = true
    this.authenticationService.recordAuthDiagnosticEvent('interactive_reauth_redirect', {
      target: redirectTarget,
    })
    this.authenticationService.loginWithRememberedOrganizationRedirect(redirectTarget)
  }

  private isAuthRedirectCallback(): boolean {
    const search = new URLSearchParams(window.location.search)
    return search.has('code') && search.has('state')
  }

  private syncStyleGuideDocumentScroll(): void {
    this.setStyleGuideDocumentMode(this.isStyleGuideRoute)
  }

  private setStyleGuideDocumentMode(active: boolean): void {
    if (typeof document === 'undefined') return

    document.documentElement.classList.toggle(AppComponent.STYLE_GUIDE_DOCUMENT_CLASS, active)
    document.body.classList.toggle(AppComponent.STYLE_GUIDE_DOCUMENT_CLASS, active)

    if (!active) return

    for (const element of [document.documentElement, document.body]) {
      for (const property of this.styleGuideDocumentStyleKeys) {
        element.style.removeProperty(property)
      }
    }
  }

  private startAuthCallbackRecoveryWatch(): void {
    if (!this.isAuthRedirectCallback()) return

    timer(AppComponent.AUTH_CALLBACK_LOADING_TIMEOUT_MS).pipe(
      switchMap(() => combineLatest([
        this.authenticationService.isLoading$,
        this.authenticationService.isAuthenticated$,
      ]).pipe(take(1))),
    ).subscribe(([isLoading, isAuthenticated]) => {
      if (!this.isAuthRedirectCallback()) return
      if (isAuthenticated) {
        this.redirectAfterCompletedAuthCallback()
        return
      }

      this.showAuthCallbackRecovery(isLoading ? 'loading_timeout' : 'callback_unauthenticated')
    })
  }

  private redirectAfterCompletedAuthCallback(): void {
    const target = this.normalizeAuthRedirectTarget(this.authenticationService.consumeAuthRedirectFallbackTarget())

    this.authenticationService.recordAuthDiagnosticEvent('auth_callback_authenticated_redirect', { target })
    this._router.navigateByUrl(target, { replaceUrl: true })
  }

  private normalizeAuthRedirectTarget(target: string | null): string {
    const fallback = '/projects'
    const trimmed = target?.trim()

    if (!trimmed) return fallback
    if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('//')) return fallback

    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  }

  private showAuthCallbackRecovery(reason: 'auth0_error' | 'callback_unauthenticated' | 'loading_timeout', detail: Record<string, unknown> = {}): void {
    if (this.isAuthCallbackRecoveryVisible) return

    this.isAuthCallbackRecoveryVisible = true
    this.authenticationService.recordAuthDiagnosticEvent('auth_callback_recovery_visible', {
      path: window.location.pathname || '/',
      reason,
      ...detail,
    })
  }

  private getAuthCallbackErrorDetail(err: any): Record<string, unknown> {
    return {
      error: err?.error ?? err?.cause?.error ?? null,
      name: err?.name ?? err?.cause?.name ?? null,
    }
  }

  retryLoginAfterAuthCallback(): void {
    this.authenticationService.recordAuthDiagnosticEvent('auth_callback_recovery_retry', {
      path: window.location.pathname || '/',
    })
    this.isAuthCallbackRecoveryVisible = false
    this._router.navigate(['/login'], {
      queryParams: { authCallbackRecovery: '1' },
      replaceUrl: true,
    })
  }

  private isPublicViewerRoute(): boolean {
    return this.authenticationService.currentModule === 'viewer'
      && this.authenticationService.projectPublicStatus === true
  }

  closeContextMenu() {
    this.contextMenuService.closeContext()
  }

  isValidEmail(email: string): boolean {
    // ^          - Asserts the start of the string.
    // [^\s @]+   - Matches one or more characters that are not whitespace(\s) or the '@' symbol.
    // @          - Matches the '@' symbol.
    // [^\s @]+   - Matches one or more characters that are not whitespace(\s) or the '@' symbol.
    // \.         - Escapes the '.' symbol and matches a literal dot.
    // [^\s @]+   - Matches one or more characters that are not whitespace(\s) or the '@' symbol.
    // $          - Asserts the end of the string.
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  updateUser(user: any) {
    // if (this.isValidEmail(user.email)) {
    this.userService.updateUser(user).subscribe()
    // }
  }

  navigateToFeedback() {
    // Dismiss any active modals to avoid aria-hidden errors
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }

    // Get the current URL for the `redirect` query parameter
    const currentUrl = this._router.url

    // Navigate to the feedback page with the current URL as a redirect parameter
    this._router.navigate(['/feedback'], {
      queryParams: { redirect: currentUrl }
    })
  }

}
